/**
 * Track-level mastery aggregation for the v2 Skill Radar.
 *
 * A "track" groups curriculum nodes by subject area (Pitch, Modes, …).
 * These functions aggregate per-item FSRS card state up to track level
 * so the Stats radar and the /practice mini-radar tile share one source
 * of truth.
 *
 * Only 5 tracks are active in the current curriculum; "counterpoint" nodes
 * don't exist yet so it is excluded from RADAR_TRACKS until they ship.
 */

import type { CurriculumNode, Item, Track } from "./item-library.js";
import { MVP_NODES } from "./item-library.js";
import { MASTERY_PRESETS, type CardStore, type MasteryThresholds } from "./scheduler.js";

/**
 * The 5 ear-training tracks the radar represents. `counterpoint` has no nodes
 * yet; `guitar` is an opt-in instrument track that renders in its own Progress
 * section, never as a radar spoke (docs/design/guitar-harmony-v1.md §5.1).
 */
export type ActiveTrack = Exclude<Track, "counterpoint" | "guitar">;

/**
 * Fixed clockwise spoke order from 12 o'clock, per stats-design-v2.md §4.3.
 * Do not reorder — the position of a spoke is part of the visual identity.
 */
export const RADAR_TRACKS: readonly ActiveTrack[] = [
  "pitch",
  "modes",
  "intervals",
  "harmony",
  "dictation",
];

const TRACK_LABELS: Record<ActiveTrack, string> = {
  pitch: "Pitch",
  modes: "Modes",
  intervals: "Intervals",
  harmony: "Harmony",
  dictation: "Dictation",
};

export interface TrackMastery {
  track: ActiveTrack;
  label: string;
  /** 0..1 — mastered items / total items in this track. */
  mastery: number;
  items: { mastered: number; total: number };
}

/**
 * Compute mastery for a single track.
 *
 * "Mastered" uses the same stability threshold as itemTier() in scheduler.ts —
 * card.stability ≥ thresholds.mastered — aggregated to track level.
 * The fraction is mastered/total so the spoke length equals what a user
 * would compute by counting checkmarks in the skill tree.
 */
export function trackMastery(
  track: ActiveTrack,
  items: Item[],
  cards: CardStore,
  thresholds: MasteryThresholds = MASTERY_PRESETS.standard,
  nodes: CurriculumNode[] = MVP_NODES,
): TrackMastery {
  const trackItems = items.filter(
    (i) => nodes.find((n) => n.id === i.nodeId)?.track === track,
  );
  const mastered = trackItems.filter(
    (i) => (cards[i.id]?.stability ?? 0) >= thresholds.mastered,
  ).length;
  return {
    track,
    label: TRACK_LABELS[track],
    mastery: trackItems.length === 0 ? 0 : mastered / trackItems.length,
    items: { mastered, total: trackItems.length },
  };
}

/**
 * Return TrackMastery for all 5 active tracks in fixed radar order.
 *
 * This is the canonical input for both the full /stats-v2 radar and the
 * mini-radar tile on /practice-v2. Always call this rather than calling
 * trackMastery() individually so spoke order stays consistent.
 */
export function radarData(
  items: Item[],
  cards: CardStore,
  thresholds: MasteryThresholds = MASTERY_PRESETS.standard,
  nodes: CurriculumNode[] = MVP_NODES,
): TrackMastery[] {
  return RADAR_TRACKS.map((track) =>
    trackMastery(track, items, cards, thresholds, nodes),
  );
}
